import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  useTheme,
  IconButton,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
  alpha,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonGroup,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  Bookmark as PresetIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { ChangeOrderReport } from './ChangeOrderReport';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useReportPresetsStore } from './reportPresets.store';
import ProfitWarning, {
  ProfitMargins,
  calculateWarningLevel,
} from '../closeout/ProfitWarningSystem';
import { useUpdateChangeOrderMutation } from '../../hooks/useCreateChangeOrderMutation';
import { useUpdateEstimateMutation } from '../../hooks/useCreateProjectEstimateMutation';
import MaximizedTextEditor from './MaximizedTextEditor';
import { useFetchAccount } from '../../hooks/useFetchAccount';
import { useFetchUser } from '../../hooks/useFetchUsers';
import {
  useGetTotalFinalPriceCloseouts,
  useGetTotalFinalPriceChangeOrderCloseouts,
} from '../../hooks/useCloseoutHooks';
import { downloadPDF } from './ChangeOrderReportPDF';
import { getMaterialBreakdownForReport } from '../closeout/extension/aggregationUtils';

interface ChangeOrderReportDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ChangeOrderReportDialog: React.FC<ChangeOrderReportDialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { darkMode } = useAppTheme();
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const selectedContract = useStore(useUnityBuildStore).selectedContract;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const setSelectedChangeOrder = useStore(useUnityBuildStore).setSelectedChangeOrder;
  const setSelectedEstimate = useStore(useUnityBuildStore).setSelectedEstimate;

  //get user data
  const { data: userData } = useFetchUser();

  //get account data
  const { data: accountData } = useFetchAccount();

  // Get closeout data for profit margin calculations
  const { data: estimateCloseouts } = useGetTotalFinalPriceCloseouts();
  const { data: changeOrderCloseouts } = useGetTotalFinalPriceChangeOrderCloseouts();
  const closeoutData = selectedEstimate?.estimateid ? estimateCloseouts : changeOrderCloseouts;

  // Use the preset store
  const {
    currentSettings,
    presets,
    activePresetId,
    isModified,
    updateSetting,
    updateSettings,
    savePreset,
    updatePreset,
    loadPreset,
    deletePreset,
  } = useReportPresetsStore();

  // Mutation for updating change order
  const { mutateAsync: updateChangeOrder } = useUpdateChangeOrderMutation();
  const { mutateAsync: updateEstimate } = useUpdateEstimateMutation();

  // Local state for UI
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');

  // State for work description editor
  const [workDescriptionEditorOpen, setWorkDescriptionEditorOpen] = useState(false);

  // State for profit warning dialog
  const [showProfitWarningDialog, setShowProfitWarningDialog] = useState(false);
  const [pendingPrintAction, setPendingPrintAction] = useState<'print' | 'pdf' | null>(null);

  // Determine context - are we working with an estimate or change order?
  const isEstimateContext = !!selectedEstimate;
  const currentDocument = selectedEstimate || selectedChangeOrder;
  const documentType = isEstimateContext ? 'Estimate' : 'Change Order';

  // Calculate profit margins for warning system using subtotal vs selling price
  const profitMargins = React.useMemo((): ProfitMargins | null => {
    if (!closeoutData?.closeouts) return null;

    const { closeouts } = closeoutData;

    // Get subtotal and selling price from closeout data
    const subtotalCost = closeouts.totalcost || 0;
    const sellingPrice =
      closeouts.sellingprice ||
      closeouts.totalfinalprice ||
      subtotalCost + (closeouts.totaloverhead || 0) + (closeouts.totalprofit || 0);
    const markupAmount = sellingPrice - subtotalCost;

    // Calculate markup percentage: (selling price - subtotal) / subtotal * 100
    const markupPercent = subtotalCost > 0 ? (markupAmount / subtotalCost) * 100 : 0;

    return {
      markupPercent,
      subtotalCost,
      sellingPrice,
      markupAmount,
    };
  }, [closeoutData]);

  // Calculate comprehensive summary for PDF generation
  const comprehensiveSummary = React.useMemo(() => {
    if (!closeoutData?.closeouts) return null;

    const { closeouts } = closeoutData;

    // Calculate base costs from item lists
    const materialTotal =
      closeoutData?.closeouts?.extendestimate?.extendestimateList?.reduce(
        (sum: number, item: any) => sum + (item.totalcost || 0),
        0
      ) || 0;
    const quoteTotal =
      closeoutData?.closeouts?.quotecloseout?.quotecloseoutList?.reduce(
        (sum: number, item: any) => sum + (item.totalcost || 0),
        0
      ) || 0;
    const equipmentTotal =
      closeoutData?.closeouts?.equipmentcloseout?.equipmentcloseoutList?.reduce(
        (sum: number, item: any) => sum + (item.totalcost || 0),
        0
      ) || 0;
    const subcontractorTotal =
      closeoutData?.closeouts?.subcontractcloseout?.subcontractcloseoutList?.reduce(
        (sum: number, item: any) => sum + (item.totalcost || 0),
        0
      ) || 0;
    const generalExpenseTotal =
      closeoutData?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList?.reduce(
        (sum: number, item: any) => sum + (item.totalcost || 0),
        0
      ) || 0;

    // Labor calculations - include all labor types
    const directLaborList = closeouts.directlaborcloseout?.directlaborcloseoutList || [];
    const laborFactoringList = closeouts.laborfactoringcloseout?.laborfactoringcloseoutList || [];
    const incidentalLaborList =
      closeouts.incidentallaborcloseout?.incidentallaborcloseoutList || [];
    const indirectLaborList = closeouts.indirectlaborcloseout?.indirectlaborcloseoutList || [];

    // Calculate labor costs from actual data
    const directLaborCost = directLaborList.reduce(
      (sum: number, labor: any) => sum + (labor.totalcost || 0),
      0
    );
    const directLaborHours = directLaborList.reduce(
      (sum: number, labor: any) => sum + (labor.allocatedhours || 0),
      0
    );

    const laborFactoringCost = laborFactoringList.reduce(
      (sum: number, labor: any) => sum + (labor.totalcost || 0),
      0
    );
    const laborFactoringHours = laborFactoringList.reduce(
      (sum: number, labor: any) => sum + (labor.allocatedhours || 0),
      0
    );

    const incidentalLaborCost = incidentalLaborList.reduce(
      (sum: number, labor: any) => sum + (labor.totalcost || 0),
      0
    );
    const incidentalLaborHours = incidentalLaborList.reduce(
      (sum: number, labor: any) => sum + (labor.allocatedhours || 0),
      0
    );

    const indirectLaborCost = indirectLaborList.reduce(
      (sum: number, labor: any) => sum + (labor.totalcost || 0),
      0
    );
    const indirectLaborHours = indirectLaborList.reduce(
      (sum: number, labor: any) => sum + (labor.allocatedhours || 0),
      0
    );

    const totalBaseLaborCost =
      directLaborCost + laborFactoringCost + incidentalLaborCost + indirectLaborCost;
    const totalLaborHours =
      directLaborHours + laborFactoringHours + incidentalLaborHours + indirectLaborHours;

    // Use actual data from closeout calculations
    const materialCloseout = closeouts.extendestimate;
    const directLaborCloseout = closeouts.directlaborcloseout;
    const laborFactoringCloseout = closeouts.laborfactoringcloseout;
    const incidentalLaborCloseout = closeouts.incidentallaborcloseout;
    const indirectLaborCloseout = closeouts.indirectlaborcloseout;
    const equipmentCloseout = closeouts.equipmentcloseout;
    const subcontractorCloseout = closeouts.subcontractcloseout;
    const generalExpenseCloseout = closeouts.generalexpensecloseout;
    const quoteCloseout = closeouts.quotecloseout;

    // Adjusted totals from closeouts - combine all labor types
    const adjustedMaterialTotal = materialCloseout?.totalcost || 0;
    const directLaborTotal = directLaborCloseout?.totalcost || 0;
    const laborFactoringTotal = laborFactoringCloseout?.totalcost || 0;
    const incidentalLaborTotal = incidentalLaborCloseout?.totalcost || 0;
    const indirectLaborTotal = indirectLaborCloseout?.totalcost || 0;
    const adjustedLaborTotal =
      directLaborTotal + laborFactoringTotal + incidentalLaborTotal + indirectLaborTotal;
    const adjustedEquipmentTotal = equipmentCloseout?.totalcost || 0;
    const adjustedSubcontractorTotal = subcontractorCloseout?.totalcost || 0;
    const adjustedQuoteTotal = quoteCloseout?.totalcost || 0;
    const adjustedGeneralExpenseTotal = generalExpenseCloseout?.totalcost || 0;

    // Tax, overhead, and markup
    const materialTax = materialCloseout?.adjustedcost || 0;
    const materialOverhead = materialCloseout?.overheadcost || 0;
    const materialMarkup = materialCloseout?.profitcost || 0;

    const quoteTax = quoteCloseout?.adjustedcost || 0;
    const quoteOverhead = quoteCloseout?.overheadcost || 0;
    const quoteMarkup = quoteCloseout?.profitcost || 0;

    // Labor adjustments - combine all labor closeout adjustments
    const laborTax =
      (directLaborCloseout?.adjustedcost || 0) +
      (laborFactoringCloseout?.adjustedcost || 0) +
      (incidentalLaborCloseout?.adjustedcost || 0) +
      (indirectLaborCloseout?.adjustedcost || 0);
    const laborOverhead =
      (directLaborCloseout?.overheadcost || 0) +
      (laborFactoringCloseout?.overheadcost || 0) +
      (incidentalLaborCloseout?.overheadcost || 0) +
      (indirectLaborCloseout?.overheadcost || 0);
    const laborMarkup =
      (directLaborCloseout?.profitcost || 0) +
      (laborFactoringCloseout?.profitcost || 0) +
      (incidentalLaborCloseout?.profitcost || 0) +
      (indirectLaborCloseout?.profitcost || 0);

    const equipmentTax = equipmentCloseout?.adjustedcost || 0;
    const equipmentOverhead = equipmentCloseout?.overheadcost || 0;
    const equipmentMarkup = equipmentCloseout?.profitcost || 0;

    const subcontractorTax = subcontractorCloseout?.adjustedcost || 0;
    const subcontractorOverhead = subcontractorCloseout?.overheadcost || 0;
    const subcontractorMarkup = subcontractorCloseout?.profitcost || 0;

    const generalExpenseTax = generalExpenseCloseout?.adjustedcost || 0;
    const generalExpenseOverhead = generalExpenseCloseout?.overheadcost || 0;
    const generalExpenseMarkup = generalExpenseCloseout?.profitcost || 0;

    // Use actual calculated totals from the system
    const subtotal = closeouts.totalcost || 0;
    const projectOverhead = closeouts.totaloverhead || 0;
    const profit = closeouts.totalprofit || 0;
    const grandTotal =
      closeouts.sellingprice || closeouts.totalfinalprice || subtotal + projectOverhead + profit;

    return {
      materialTotal,
      materialTax,
      materialOverhead,
      materialMarkup,
      adjustedMaterialTotal,
      totalLaborHours,
      directLaborCost,
      directLaborHours,
      totalBaseLaborCost,
      laborTax,
      laborOverhead,
      laborMarkup,
      adjustedLaborTotal,
      equipmentTotal,
      equipmentTax,
      equipmentOverhead,
      equipmentMarkup,
      adjustedEquipmentTotal,
      subcontractorTotal,
      subcontractorTax,
      subcontractorOverhead,
      subcontractorMarkup,
      adjustedSubcontractorTotal,
      quoteTotal,
      quoteTax,
      quoteOverhead,
      quoteMarkup,
      adjustedQuoteTotal,
      generalExpenseTotal,
      generalExpenseTax,
      generalExpenseOverhead,
      generalExpenseMarkup,
      adjustedGeneralExpenseTotal,
      subtotal,
      projectOverhead,
      profit,
      grandTotal,
    };
  }, [closeoutData]);

  // Custom preset applications
  const applyDetailedPresetPreservingProject = () => {
    updateSettings({
      // Detail levels ON
      showDetailedBreakdown: true,
      includeLaborRates: true,
      showTaxDetails: true,
      showOverheadMarkup: true,
      showBaseCostColumn: true,
      showBurdenDetails: true,
      showAdjustmentDetails: true,

      // All sections ON
      showMaterialsSection: true,
      showQuotesSection: true,
      showLaborSection: true,
      showEquipmentSection: true,
      showSubcontractorSection: true,
      showGeneralExpensesSection: true,

      // All contractual clauses ON for detailed reports
      showGlobalSupplyChainClause: true,
      showTariffsAndDutiesClause: true,
      showContractualClausesSection: true,
      showStandardContractTerms: true,

      // Totals breakdown ON
      showProjectTotalsBreakdown: true,
    });
  };

  const applySimplePresetPreservingProject = () => {
    updateSettings({
      // Detail levels – keep ON (inherit defaults)
      showDetailedBreakdown: true,
      includeLaborRates: true,
      showTaxDetails: true,
      showOverheadMarkup: true,
      showBaseCostColumn: true,
      showBurdenDetails: true,
      showAdjustmentDetails: true,

      // Disable all report sections above Cost Summary
      showMaterialsSection: false,
      showQuotesSection: false,
      showLaborSection: false,
      showEquipmentSection: false,
      showSubcontractorSection: false,
      showGeneralExpensesSection: false,

      // Only basic contractual terms for simple reports
      showGlobalSupplyChainClause: false,
      showTariffsAndDutiesClause: false,
      showContractualClausesSection: true,
      showStandardContractTerms: true,

      // Cost summary still breaks down subtotal/overhead/profit
      showProjectTotalsBreakdown: true,
      showSubtotalInCostSummary: true,
    });
  };

  const applyMinimalPresetPreservingProject = () => {
    updateSettings({
      // Hide all sections for minimal preset
      showMaterialsSection: false,
      showQuotesSection: false,
      showLaborSection: false,
      showEquipmentSection: false,
      showSubcontractorSection: false,
      showGeneralExpensesSection: false,

      // No contractual clauses for minimal reports
      showGlobalSupplyChainClause: false,
      showTariffsAndDutiesClause: false,
      showContractualClausesSection: false,
      showStandardContractTerms: true,

      // Minimal detail level
      showDetailedBreakdown: false,
      includeLaborRates: false,
      showTaxDetails: false,
      showOverheadMarkup: false,
      showBaseCostColumn: false,
      showBurdenDetails: false,
      showAdjustmentDetails: false,
      showItemBreakouts: false,
      showQuantityDetails: false,
      showUnitCosts: false,
      showLaborHours: false,
      showNotes: false,
      compactView: true,

      // Totals breakdown
      showProjectTotalsBreakdown: false,

      // Turn off extension breakouts, subtotal, and notes for minimal preset
      showExtensionBreakouts: false,
      showSubtotalInCostSummary: false,
      showNotesInTables: false,

      // Set all cost summary categories to false in the minimal preset
      showExtensionMaterialsInCostSummary: false,
      showQuotedMaterialsInCostSummary: false,
      showLaborInCostSummary: false,
      showEquipmentInCostSummary: false,
      showSubcontractorsInCostSummary: false,
      showGeneralExpensesInCostSummary: false,
    });
  };

  const resetToDefaultsPreservingProject = () => {
    updateSettings({
      showDetailedBreakdown: true,
      includeLaborRates: true,
      showTaxDetails: true,
      showOverheadMarkup: true,
      showBaseCostColumn: true,
      showMaterialsSection: true,
      showQuotesSection: true,
      showLaborSection: true,
      showEquipmentSection: true,
      showSubcontractorSection: true,
      showGeneralExpensesSection: true,
      showGlobalSupplyChainClause: true,
      showTariffsAndDutiesClause: true,
      showContractualClausesSection: true,
      showStandardContractTerms: true,
      showProjectTotalsBreakdown: true,
      // Reset cost summary categories to defaults (all true)
      showExtensionMaterialsInCostSummary: true,
      showQuotedMaterialsInCostSummary: true,
      showLaborInCostSummary: true,
      showEquipmentInCostSummary: true,
      showSubcontractorsInCostSummary: true,
      showGeneralExpensesInCostSummary: true,
    });
  };

  const loadPresetPreservingProject = (presetId: string) => {
    loadPreset(presetId);
  };

  const handleSettingChange = <K extends keyof typeof currentSettings>(
    key: K,
    value: (typeof currentSettings)[K]
  ) => {
    updateSetting(key, value);
  };

  const handleSavePreset = () => {
    if (newPresetName.trim()) {
      savePreset(newPresetName.trim(), newPresetDescription.trim());
      setNewPresetName('');
      setNewPresetDescription('');
    }
  };

  const handleUpdatePreset = () => {
    if (activePresetId) {
      updatePreset(activePresetId);
    }
  };

  // Helper functions for expiration date
  const calculateExpirationDate = (days: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  const getEffectiveExpirationDate = (): Date | null => {
    if (!currentSettings.showExpirationDate) return null;
    return (
      currentSettings.customExpirationDate ||
      calculateExpirationDate(currentSettings.expirationDays)
    );
  };

  const handleExpirationDaysChange = (days: number) => {
    handleSettingChange('expirationDays', Math.max(0, days));
    // Clear custom date when changing days
    handleSettingChange('customExpirationDate', null);
  };

  const handleCustomExpirationDateChange = (dateString: string) => {
    if (dateString) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        handleSettingChange('customExpirationDate', date);
      }
    } else {
      handleSettingChange('customExpirationDate', null);
    }
  };

  // Handlers for work description updates
  const handleWorkDescriptionUpdate = async (newDescription: string) => {
    if (isEstimateContext && selectedEstimate && newDescription !== selectedEstimate.description) {
      try {
        const updatedEstimate = {
          ...selectedEstimate,
          description: newDescription,
        };
        await updateEstimate(updatedEstimate);
        setSelectedEstimate(updatedEstimate);
      } catch (error) {
        console.error('Error updating Estimate Description:', error);
      }
    } else if (
      !isEstimateContext &&
      selectedChangeOrder &&
      newDescription !== selectedChangeOrder.description
    ) {
      try {
        const updatedChangeOrder = {
          ...selectedChangeOrder,
          description: newDescription,
        };
        await updateChangeOrder(updatedChangeOrder);
        setSelectedChangeOrder(updatedChangeOrder);
      } catch (error) {
        console.error('Error updating Work Description:', error);
      }
    }
  };

  const handleWorkDescriptionSave = (value: string) => {
    handleWorkDescriptionUpdate(value);
    setWorkDescriptionEditorOpen(false);
  };

  // Check profit margins before print/PDF actions
  const checkProfitMarginsBeforeAction = (action: 'print' | 'pdf'): boolean => {
    if (!profitMargins) return true; // No data available, allow action

    const warningLevel = calculateWarningLevel(profitMargins);

    if (warningLevel === 'critical' || warningLevel === 'warning') {
      setPendingPrintAction(action);
      setShowProfitWarningDialog(true);
      return false; // Block the action to show warning first
    }

    return true; // Safe to proceed
  };

  // Execute the pending action after user confirms
  const executePendingAction = () => {
    if (pendingPrintAction === 'print') {
      executePrint();
    } else if (pendingPrintAction === 'pdf') {
      executeDownloadPDF();
    }
    setShowProfitWarningDialog(false);
    setPendingPrintAction(null);
  };

  const handlePrint = () => {
    if (checkProfitMarginsBeforeAction('print')) {
      executePrint();
    }
  };

  const executePrint = () => {
    // Create a comprehensive print implementation
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Get the report content
    const reportElement = document.querySelector('.change-order-report-content');
    if (!reportElement) return;

    // Create comprehensive print styles that match the light mode preview exactly
    const printStyles = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${isEstimateContext ? 'Estimate' : 'Change Order'} Report - ${currentDocument?.customid || 'New'}</title>
        <meta charset="utf-8">
        <style>
          /* Reset and base styles */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          @page {
            margin: 0.5in;
            size: letter;
          }
          
          body {
            font-family: 'Segoe UI', 'Roboto', 'Arial', sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333333;
            background-color: #ffffff;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Force light mode styling for all elements */
          .MuiPaper-root,
          .change-order-report-content > div,
          .report-container {
            background-color: #ffffff !important;
            color: #333333 !important;
            box-shadow: none !important;
          }
          
          /* Typography styles */
          h1, h2, h3, h4, h5, h6 {
            font-weight: bold;
            margin-bottom: 8px;
          }
          
          h4 {
            font-size: 18px;
            text-align: center;
            margin-bottom: 24px;
          }
          
          h6 {
            font-size: 14px;
          }
          
          /* Section headers */
          .section-header,
          [class*="MuiBox-root"]:has(> [class*="MuiTypography-subtitle1"]),
          div:has(> .MuiTypography-subtitle1) {
            background-color: #1976d2 !important;
            color: #ffffff !important;
            padding: 8px 16px;
            margin-top: 16px;
            margin-bottom: 8px;
            font-weight: bold;
            font-size: 14px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            page-break-inside: avoid;
          }
          
          /* Hide expand/collapse icons in print */
          .section-header button,
          .section-header .MuiIconButton-root,
          [class*="MuiIconButton-root"] {
            display: none !important;
          }
          
          /* Table styles */
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 16px;
            background-color: #ffffff !important;
            page-break-inside: avoid;
          }
          
          th, td {
            border: 1px solid #e0e0e0 !important;
            padding: 4px 8px;
            font-size: 10px;
            text-align: left;
            background-color: #ffffff !important;
            color: #333333 !important;
          }
          
          th {
            background-color: #e3f2fd !important;
            color: #333333 !important;
            font-weight: bold;
          }
          
          .total-row td {
            background-color: #e3f2fd !important;
            color: #333333 !important;
            font-weight: bold;
          }
          
          /* Fix adjustments column layout - force vertical stacking */
          td span[style*="display: block"],
          .MuiBox-root[component="span"] {
            display: block !important;
            margin-bottom: 2px !important;
            line-height: 1.2 !important;
          }
          
          /* Ensure adjustment items stack vertically */
          td > span,
          td .MuiBox-root {
            display: block !important;
            width: 100% !important;
          }
          
          /* Force line breaks between adjustment items */
          td span + span,
          td .MuiBox-root + .MuiBox-root {
            margin-top: 2px !important;
          }
          
          /* Cost Summary table specific adjustments column */
          table:last-of-type td:nth-child(3) span,
          table:last-of-type td:nth-child(3) .MuiBox-root,
          .cost-summary td[align="right"]:nth-child(3) span,
          .cost-summary td[align="right"]:nth-child(3) .MuiBox-root {
            display: block !important;
            white-space: nowrap !important;
            margin-bottom: 3px !important;
          }
          
          /* Prevent inline display in adjustments */
          td:nth-child(3) > span {
            display: block !important;
            clear: both !important;
          }
          
          /* Grid and layout */
          .MuiGrid-container {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            margin-bottom: 16px;
          }
          
          .MuiGrid-item {
            flex: 1;
            min-width: 300px;
          }
          
          /* Boxes and containers */
          .summary-box,
          div[class*="MuiBox-root"] {
            border: 1px solid #e0e0e0 !important;
            padding: 16px;
            margin-bottom: 8px;
            background-color: #ffffff !important;
            color: #333333 !important;
          }
          
          /* Work description box */
          .work-description {
            border: 1px solid #e0e0e0 !important;
            padding: 16px;
            margin-bottom: 24px;
            background-color: #ffffff !important;
            min-height: 60px;
          }
          
          /* Work Description Section Page Break Control */
          /* Keep "Proposed Change Order" title with work description */
          h4[style*="text-align: center"],
          .MuiTypography-h4[style*="text-align: center"] {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          /* Work Description section should not break from previous content */
          .section-header {
            page-break-before: avoid !important;
            break-before: avoid !important;
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          /* Keep the first few sections together as a logical unit to prevent header separation */
          .print-content > div:nth-child(1),
          .print-content > div:nth-child(2),
          .print-content > div:nth-child(3),
          .print-content > div:nth-child(4) {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Specifically target the work description area to prevent breaks */
          .MuiCollapse-root {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Report title and work description container - keep as one unit */
          .report-title-and-description {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-before: avoid !important;
            break-before: avoid !important;
          }
          
          /* Rich text content in work description */
          .work-description h1,
          .work-description h2,
          .work-description h3,
          .work-description h4,
          .work-description h5,
          .work-description h6 {
            color: #333333 !important;
            margin: 8px 0 4px 0 !important;
            font-weight: bold !important;
            page-break-after: avoid !important;
          }
          
          .work-description h1 { font-size: 16px !important; }
          .work-description h2 { font-size: 15px !important; }
          .work-description h3 { font-size: 14px !important; }
          .work-description h4 { font-size: 13px !important; }
          
          .work-description p {
            color: #333333 !important;
            margin: 4px 0 !important;
            line-height: 1.4 !important;
          }
          
          .work-description ul,
          .work-description ol {
            color: #333333 !important;
            margin: 4px 0 !important;
            padding-left: 20px !important;
          }
          
          .work-description li {
            color: #333333 !important;
            margin: 2px 0 !important;
          }
          
          .work-description strong {
            font-weight: bold !important;
            color: #333333 !important;
          }
          
          .work-description em {
            font-style: italic !important;
            color: #333333 !important;
          }
          
          .work-description u {
            text-decoration: underline !important;
            color: #333333 !important;
          }
          
          /* Project totals special styling */
          .project-totals {
            border: 2px solid #1976d2 !important;
            background-color: #e3f2fd !important;
            color: #333333 !important;
            padding: 16px;
            margin-bottom: 16px;
          }
          
          /* Signature sections */
          .signature-section {
            border: 1px solid #e0e0e0 !important;
            padding: 16px;
            margin-bottom: 16px;
            background-color: #ffffff !important;
            page-break-inside: avoid;
          }
          
          .signature-line {
            border-bottom: 1px solid #333333 !important;
            height: 40px;
            margin-bottom: 8px;
          }
          
          /* Form fields - hide in print */
          input[type="text"],
          input[type="date"],
          .MuiTextField-root,
          .MuiInputBase-root {
            display: none !important;
          }
          
          /* Dividers */
          hr, .MuiDivider-root {
            border: none;
            border-top: 1px solid #e0e0e0;
            margin: 8px 0;
          }
          
          /* Page break controls */
          .page-break-before {
            page-break-before: always;
          }
          
          .page-break-after {
            page-break-after: always;
          }
          
          .no-page-break {
            page-break-inside: avoid;
          }
          
          /* Signature sections - prevent page breaks */
          .signature-section,
          div:has(h6:contains("Contractor Certification")),
          div:has(h6:contains("Client Acceptance")) {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 20px;
          }
          
          /* More specific targeting for signature sections */
          .print-content > div > div:nth-last-child(-n+3) {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Section headers with their content */
          div[style*="background-color: rgb(25, 118, 210)"],
          div[style*="backgroundColor"],
          .section-header {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }
          
          .section-header + div,
          .section-header + .MuiCollapse-root {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }
          
          /* Cost Summary table protection */
          .cost-summary table,
          table:last-of-type {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Prevent orphaned elements */
          .print-content > div:last-child,
          .print-content > div:nth-last-child(2),
          .print-content > div:nth-last-child(3) {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Report footer */
          .report-footer,
          div:has(span:contains("Report Generated")) {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-top: 20px;
          }
          
          /* Additional robust signature section protection */
          [class*="signature"], 
          .MuiGrid-container:nth-last-child(-n+4),
          div:nth-last-child(-n+4) {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Smart grouping for logical sections */
          .print-content h6 + div,
          .print-content .MuiTypography-h6 + div {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }
          
          /* Prevent breaks in form-like content */
          .MuiGrid-container {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Table row protection (additional) */
          tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          /* Section grouping - keep headers with content */
          div + div:has(table),
          .section-header ~ div:first-of-type {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }
          
          /* Header section */
          .report-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 24px;
            border-bottom: 2px solid #e0e0e0;
            padding-bottom: 16px;
            page-break-inside: avoid;
          }
          
          .report-header .left-section {
            flex: 1;
          }
          
          .report-header .right-section {
            text-align: right;
            flex: 1;
          }
          
          /* Remove unwanted boxes from header only */
          .report-header > div {
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          
          /* Specific print header layout - ensure side by side */
          .print-content > div:first-child > div:first-child {
            display: flex !important;
            justify-content: space-between !important;
            align-items: flex-start !important;
            width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          
          .print-content > div:first-child > div:first-child > div:first-child {
            flex: 1 !important;
            padding-right: 20px !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          
          .print-content > div:first-child > div:first-child > div:last-child {
            flex: 1 !important;
            text-align: right !important;
            padding-left: 20px !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          
          /* Remove all possible border styles from header elements */
          .print-content > div:first-child > div:first-child *,
          .print-content > div:first-child > div:first-child > div > *,
          .MuiBox-root:first-child .MuiBox-root,
          div[style*="borderBottom"] > div,
          div[style*="border-bottom"] > div {
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
            outline: none !important;
          }
          
          /* Ensure Work Description and Extension Materials sections are clean */
          .MuiCollapse-root .MuiBox-root:not(.MuiTableContainer-root) {
            border: none !important;
            background: transparent !important;
          }
          
          /* Footer */
          .report-footer {
            margin-top: 32px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
            text-align: center;
            font-size: 10px;
          }
          
          /* Ensure all collapsed sections are expanded for print */
          .MuiCollapse-root {
            height: auto !important;
            overflow: visible !important;
          }
          
          .MuiCollapse-wrapper {
            height: auto !important;
          }
          
          .MuiCollapse-wrapperInner {
            height: auto !important;
          }
          
          /* Hide no-print elements */
          .no-print {
            display: none !important;
          }
          
          /* Ensure text is visible */
          .MuiTypography-root {
            color: #333333 !important;
          }
          
          /* Avoid orphans and widows */
          p, div {
            orphans: 2;
            widows: 2;
          }
          
          /* Large tables should allow breaks between rows but not within rows */
          tbody tr {
            page-break-inside: avoid;
          }
          
          /* Ensure section headers stay with their content */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
          }
          
          /* Print-specific size adjustments */
          @media print {
            body {
              font-size: 11px;
            }
            
            th, td {
              font-size: 9px;
              padding: 3px 6px;
            }
            
            .section-header {
              font-size: 12px;
              padding: 6px 12px;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-content">
    `;

    // Get the report content and modify it for print
    let reportContent = reportElement.innerHTML;

    // Expand all collapsed sections for print by removing collapse classes
    reportContent = reportContent.replace(/style="[^"]*display:\s*none[^"]*"/gi, '');
    reportContent = reportContent.replace(/class="[^"]*MuiCollapse-hidden[^"]*"/gi, '');

    // Add print-specific classes to improve page breaks
    reportContent = reportContent.replace(
      /<div[^>]*class="[^"]*section-header[^"]*"[^>]*>/gi,
      '$& class="no-page-break"'
    );

    // Close the HTML structure
    const printFooter = `
        </div>
        <script>
          // Ensure all content is loaded before printing
          window.onload = function() {
            // Small delay to ensure rendering is complete
            setTimeout(function() {
              window.focus();
              window.print();
              // Don't close automatically to allow user to save as PDF
            }, 500);
          };
          
          // Handle print dialog close
          window.onafterprint = function() {
            // Optionally close the window after printing
            // window.close();
          };
        </script>
      </body>
      </html>
    `;

    // Write the complete HTML to the print window
    printWindow.document.write(printStyles + reportContent + printFooter);
    printWindow.document.close();
  };

  const handleDownloadPDF = () => {
    if (checkProfitMarginsBeforeAction('pdf')) {
      executeDownloadPDF();
    }
  };

  const executeDownloadPDF = () => {
    // Prepare data for PDF generation
    const pdfData = {
      companyName: accountData?.account?.accountname || 'Unity Build Technologies',
      workDescription: currentDocument?.description || '',
      pcoNumber:
        (isEstimateContext ? selectedEstimate?.customid : selectedChangeOrder?.customid) || '',
      isEstimateContext,
      userData,
      accountData,
      projectData: selectedProject,
      contractData: selectedContract,
      estimateData: selectedEstimate,
      changeOrderData: selectedChangeOrder,
      materialBreakdown: closeoutData?.closeouts?.extendestimate?.extendestimateList
        ? getMaterialBreakdownForReport(
            closeoutData.closeouts.extendestimate.extendestimateList as any,
            !currentSettings.showExtensionBreakouts
          )
        : [],
      quoteBreakdown:
        closeoutData?.closeouts?.quotecloseout?.quotecloseoutList?.map(item => ({
          id: item.closeoutid || '',
          name: item.name || 'Unknown Quote',
          quotedCost: item.quotedcost || 0,
          adjustmentPercent: item.adjustedpercent || 0,
          totalCost: item.totalcost || 0,
          notes: item.notes || '',
        })) || [],
      laborBreakdown: closeoutData?.closeouts?.directlaborcloseout?.directlaborcloseoutList || [],
      closeoutData: closeoutData, // Pass the full closeout data for PDF access
      equipmentBreakdown:
        closeoutData?.closeouts?.equipmentcloseout?.equipmentcloseoutList?.map(item => ({
          id: item.closeoutid || '',
          name: item.name || 'Unknown Equipment',
          quantity: item.quantity || 0,
          duration: item.duration || 0,
          unitCost: item.unitcost || 0,
          totalCost: item.totalcost || 0,
          notes: item.notes || '',
        })) || [],
      subcontractorBreakdown:
        closeoutData?.closeouts?.subcontractcloseout?.subcontractcloseoutList?.map(item => ({
          id: item.closeoutid || '',
          name: item.name || 'Unknown Subcontractor',
          description: item.notes || '',
          quotedAmount: item.quotedcost || 0,
          totalCost: item.totalcost || 0,
          notes: item.notes || '',
        })) || [],
      generalExpenseBreakdown:
        closeoutData?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList?.map(item => ({
          id: item.closeoutid || '',
          name: item.name || 'Unknown Expense',
          description: '',
          quantity: item.quantity || 0,
          unitCost: item.unitcost || 0,
          totalCost: item.totalcost || 0,
          notes: item.notes || '',
        })) || [],
      comprehensiveSummary: comprehensiveSummary,
      expirationDate: getEffectiveExpirationDate(),
    };

    const pdfSettings = currentSettings;

    const filename = `${isEstimateContext ? 'Estimate' : 'ChangeOrder'}_${
      (isEstimateContext ? selectedEstimate?.customid : selectedChangeOrder?.customid) || 'Report'
    }_${new Date().toISOString().split('T')[0]}.pdf`;

    downloadPDF(pdfData, pdfSettings, filename);
  };

  // If not open, don't render anything
  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200, // Below chat window
        bgcolor: darkMode ? '#1a1a1a' : '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: darkMode ? '#2c2c2c' : '#f5f5f5',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '48px',
          px: 4,
          py: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '18px' }}>
              {documentType} Report Generator
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              {currentDocument?.name || `New ${documentType}`} •{' '}
              {selectedContract?.contractname || 'No Contract'} •{' '}
              {selectedProject?.name || 'No Project'}
            </Typography>
          </Box>
          {activePresetId && !presets.find(p => p.id === activePresetId)?.isDefault && (
            <Chip
              label={`${presets.find(p => p.id === activePresetId)?.name || 'Custom'}${isModified ? ' (Modified)' : ''}`}
              color={isModified ? 'warning' : 'primary'}
              icon={<PresetIcon />}
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {profitMargins && calculateWarningLevel(profitMargins) !== 'none' && (
            <Chip
              label={
                calculateWarningLevel(profitMargins) === 'critical'
                  ? '🚨 Missing Margins'
                  : '⚠️ Low Margins'
              }
              color={calculateWarningLevel(profitMargins) === 'critical' ? 'error' : 'warning'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            size="small"
            color={
              profitMargins && calculateWarningLevel(profitMargins) === 'critical'
                ? 'error'
                : profitMargins && calculateWarningLevel(profitMargins) === 'warning'
                  ? 'warning'
                  : 'primary'
            }
          >
            PDF
          </Button>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.error.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ display: 'flex', height: 'calc(100vh - 48px)', overflow: 'hidden' }}>
        {/* Settings Panel - Left Side */}
        <Paper
          sx={{
            width: '450px',
            minWidth: '450px',
            bgcolor: darkMode ? '#2c2c2c' : '#f9f9f9',
            borderRadius: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            overflowY: 'auto',
            height: '100%',
          }}
        >
          <Box sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Profit & Overhead Warning */}
              {profitMargins && (
                <Grid item xs={12}>
                  <ProfitWarning
                    margins={profitMargins}
                    variant="inline"
                    context={isEstimateContext ? 'estimate' : 'changeOrder'}
                  />
                </Grid>
              )}

              {/* Work Description Editor */}
              <Grid item xs={12}>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Edit {documentType} Description 🏗️ 🧑‍🔧
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setWorkDescriptionEditorOpen(true)}
                      sx={{
                        minWidth: 'auto',
                        px: 2,
                      }}
                    >
                      Edit
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {/* Quick Presets */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Quick Presets
                </Typography>
                <ButtonGroup variant="outlined" fullWidth>
                  <Button
                    onClick={applyDetailedPresetPreservingProject}
                    variant={
                      activePresetId === 'detailed' && !isModified ? 'contained' : 'outlined'
                    }
                    color="primary"
                  >
                    Detailed
                  </Button>
                  <Button
                    onClick={applySimplePresetPreservingProject}
                    variant={activePresetId === 'simple' && !isModified ? 'contained' : 'outlined'}
                    color="primary"
                  >
                    Simple
                  </Button>
                  <Button
                    onClick={applyMinimalPresetPreservingProject}
                    variant={activePresetId === 'minimal' && !isModified ? 'contained' : 'outlined'}
                    color="primary"
                  >
                    Minimal
                  </Button>
                </ButtonGroup>
              </Grid>

              {/* Saved Presets */}
              {presets.filter(p => !p.isDefault).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Saved Presets
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {presets
                      .filter(p => !p.isDefault)
                      .map(preset => (
                        <Button
                          key={preset.id}
                          variant={
                            activePresetId === preset.id && !isModified ? 'contained' : 'outlined'
                          }
                          color="primary"
                          onClick={() => loadPresetPreservingProject(preset.id)}
                          startIcon={<PresetIcon />}
                          sx={{
                            justifyContent: 'flex-start',
                            textAlign: 'left',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              flex: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {preset.name}
                              {activePresetId === preset.id &&
                                isModified &&
                                !presets.find(p => p.id === activePresetId)?.isDefault && (
                                  <Chip
                                    label="Modified"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1, fontSize: '10px', height: '18px' }}
                                  />
                                )}
                            </Typography>
                            {preset.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {preset.description}
                              </Typography>
                            )}
                          </Box>
                        </Button>
                      ))}
                  </Box>
                </Grid>
              )}

              {/* Report Detail Level */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Detail Level</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showDetailedBreakdown}
                            onChange={e =>
                              handleSettingChange('showDetailedBreakdown', e.target.checked)
                            }
                          />
                        }
                        label="Show Detailed Table Breakdown (unit costs, quantities, etc.)"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.includeLaborRates}
                            onChange={e =>
                              handleSettingChange('includeLaborRates', e.target.checked)
                            }
                          />
                        }
                        label="Show Labor Rates"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showTaxDetails}
                            onChange={e => handleSettingChange('showTaxDetails', e.target.checked)}
                          />
                        }
                        label="Show Tax Details"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showOverheadMarkup}
                            onChange={e =>
                              handleSettingChange('showOverheadMarkup', e.target.checked)
                            }
                          />
                        }
                        label="Show Overhead & Markup"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showBaseCostColumn}
                            onChange={e =>
                              handleSettingChange('showBaseCostColumn', e.target.checked)
                            }
                          />
                        }
                        label="Show Base Cost Column in Cost Summary"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showExtensionBreakouts}
                            onChange={e =>
                              handleSettingChange('showExtensionBreakouts', e.target.checked)
                            }
                          />
                        }
                        label="Show Individual Extension Breakouts"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showSubtotalInCostSummary}
                            onChange={e =>
                              handleSettingChange('showSubtotalInCostSummary', e.target.checked)
                            }
                          />
                        }
                        label="Show Subtotal in Cost Summary"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showNotesInTables}
                            onChange={e =>
                              handleSettingChange('showNotesInTables', e.target.checked)
                            }
                          />
                        }
                        label="Show Notes in Report Sections"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Cost Summary Categories */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Cost Summary Categories</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Control which cost categories appear in the Cost Summary section. The final
                        selling price/total amount is always shown.
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showExtensionMaterialsInCostSummary}
                            onChange={e =>
                              handleSettingChange(
                                'showExtensionMaterialsInCostSummary',
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Extension Materials Row"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showQuotedMaterialsInCostSummary}
                            onChange={e =>
                              handleSettingChange(
                                'showQuotedMaterialsInCostSummary',
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Quoted Materials Row"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showLaborInCostSummary}
                            onChange={e =>
                              handleSettingChange('showLaborInCostSummary', e.target.checked)
                            }
                          />
                        }
                        label="Labor Row"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showEquipmentInCostSummary}
                            onChange={e =>
                              handleSettingChange('showEquipmentInCostSummary', e.target.checked)
                            }
                          />
                        }
                        label="Equipment Row"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showSubcontractorsInCostSummary}
                            onChange={e =>
                              handleSettingChange(
                                'showSubcontractorsInCostSummary',
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="Subcontractors Row"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showGeneralExpensesInCostSummary}
                            onChange={e =>
                              handleSettingChange(
                                'showGeneralExpensesInCostSummary',
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="General Expenses Row"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Expiration Date Settings */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Expiration Date</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showExpirationDate}
                            onChange={e =>
                              handleSettingChange('showExpirationDate', e.target.checked)
                            }
                          />
                        }
                        label="Show Expiration Date on Report"
                      />

                      {currentSettings.showExpirationDate && (
                        <>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Set Expiration Date
                            </Typography>

                            {/* Days Input */}
                            <TextField
                              label="Days from today"
                              type="number"
                              size="small"
                              value={currentSettings.expirationDays}
                              onChange={e =>
                                handleExpirationDaysChange(parseInt(e.target.value) || 0)
                              }
                              InputProps={{
                                endAdornment: <InputAdornment position="end">days</InputAdornment>,
                              }}
                              helperText="Enter number of days (30, 45, 60, etc.)"
                            />

                            <Typography
                              variant="body2"
                              sx={{ textAlign: 'center', color: 'text.secondary' }}
                            >
                              - OR -
                            </Typography>

                            {/* Custom Date Picker */}
                            <TextField
                              label="Custom expiration date"
                              type="date"
                              size="small"
                              value={
                                currentSettings.customExpirationDate
                                  ? currentSettings.customExpirationDate.toISOString().split('T')[0]
                                  : ''
                              }
                              onChange={e => handleCustomExpirationDateChange(e.target.value)}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              helperText="Choose a specific date"
                            />
                          </Box>
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Section Visibility */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Report Sections</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showMaterialsSection}
                            onChange={e =>
                              handleSettingChange('showMaterialsSection', e.target.checked)
                            }
                          />
                        }
                        label="Extension Materials Section"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showQuotesSection}
                            onChange={e =>
                              handleSettingChange('showQuotesSection', e.target.checked)
                            }
                          />
                        }
                        label="Quoted Materials Section"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showLaborSection}
                            onChange={e =>
                              handleSettingChange('showLaborSection', e.target.checked)
                            }
                          />
                        }
                        label="Labor Details Section"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showEquipmentSection}
                            onChange={e =>
                              handleSettingChange('showEquipmentSection', e.target.checked)
                            }
                          />
                        }
                        label="Equipment Section"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showSubcontractorSection}
                            onChange={e =>
                              handleSettingChange('showSubcontractorSection', e.target.checked)
                            }
                          />
                        }
                        label="Subcontractor Section"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showGeneralExpensesSection}
                            onChange={e =>
                              handleSettingChange('showGeneralExpensesSection', e.target.checked)
                            }
                          />
                        }
                        label="General Expenses Section"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Contractual Clauses */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Contractual Clauses</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showGlobalSupplyChainClause}
                            onChange={e =>
                              handleSettingChange('showGlobalSupplyChainClause', e.target.checked)
                            }
                          />
                        }
                        label="Global Supply Chain Distribution Clause"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showTariffsAndDutiesClause}
                            onChange={e =>
                              handleSettingChange('showTariffsAndDutiesClause', e.target.checked)
                            }
                          />
                        }
                        label="Tariffs, Duties, and Import Fees Clause"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={currentSettings.showStandardContractTerms}
                            onChange={e =>
                              handleSettingChange('showStandardContractTerms', e.target.checked)
                            }
                          />
                        }
                        label="Standard Contract Terms & Conditions"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Preset Management */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="h6">Preset Management</Typography>
                      {activePresetId && !presets.find(p => p.id === activePresetId)?.isDefault && (
                        <Chip
                          label={presets.find(p => p.id === activePresetId)?.name || 'Custom'}
                          size="small"
                          color={isModified ? 'warning' : 'primary'}
                          sx={{ ml: 1 }}
                        />
                      )}
                      {isModified &&
                        activePresetId &&
                        !presets.find(p => p.id === activePresetId)?.isDefault && (
                          <Chip
                            label="Unsaved Changes"
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Current Preset Info */}
                      {activePresetId && !presets.find(p => p.id === activePresetId)?.isDefault && (
                        <Box sx={{ p: 2, bgcolor: darkMode ? '#333' : '#f5f5f5', borderRadius: 1 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Editing: {presets.find(p => p.id === activePresetId)?.name}
                            </Typography>
                            {isModified && (
                              <Chip
                                label="Modified"
                                size="small"
                                color="warning"
                                sx={{ fontSize: '10px' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {presets.find(p => p.id === activePresetId)?.description}
                          </Typography>

                          {/* Update Preset Option */}
                          {isModified && (
                            <Box sx={{ mt: 2 }}>
                              <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleUpdatePreset}
                                size="small"
                                fullWidth
                                color="primary"
                              >
                                Update "{presets.find(p => p.id === activePresetId)?.name}"
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Save Current Settings */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 2 }}>
                          Save as New Preset
                        </Typography>
                        <TextField
                          fullWidth
                          label="New Preset Name"
                          value={newPresetName}
                          onChange={e => setNewPresetName(e.target.value)}
                          size="small"
                          sx={{ mb: 1 }}
                          placeholder="e.g., Client Presentation, Internal Review..."
                        />
                        <TextField
                          fullWidth
                          label="Description (optional)"
                          value={newPresetDescription}
                          onChange={e => setNewPresetDescription(e.target.value)}
                          size="small"
                          multiline
                          rows={2}
                          sx={{ mb: 2 }}
                          placeholder="Describe when to use this preset..."
                        />
                        <Button
                          variant="outlined"
                          startIcon={<SaveIcon />}
                          onClick={handleSavePreset}
                          disabled={!newPresetName.trim()}
                          size="small"
                          fullWidth
                        >
                          Save as New Preset
                        </Button>
                      </Box>

                      {/* Edit Existing Presets */}
                      <Divider />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Edit Presets
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {presets.filter(preset => !preset.isDefault).length === 0 ? (
                          <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">No custom presets saved yet.</Typography>
                            <Typography variant="caption">
                              Create your first preset using the form above.
                            </Typography>
                          </Box>
                        ) : (
                          presets
                            .filter(preset => !preset.isDefault)
                            .map(preset => (
                              <Box
                                key={preset.id}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1,
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                  bgcolor:
                                    activePresetId === preset.id
                                      ? alpha(theme.palette.primary.main, 0.1)
                                      : 'transparent',
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {preset.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {preset.description}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    onClick={() => loadPresetPreservingProject(preset.id)}
                                    variant={
                                      activePresetId === preset.id && !isModified
                                        ? 'contained'
                                        : 'outlined'
                                    }
                                    color="primary"
                                  >
                                    {activePresetId === preset.id ? 'Editing' : 'Edit'}
                                  </Button>
                                  <IconButton
                                    size="small"
                                    onClick={() => deletePreset(preset.id)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))
                        )}
                      </Box>

                      {/* Reset to Defaults */}
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={resetToDefaultsPreservingProject}
                        size="small"
                      >
                        Reset to Defaults
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Preview Panel - Right Side */}
        <Box
          sx={{
            flex: 1,
            bgcolor: darkMode ? '#1a1a1a' : '#ffffff',
            overflowY: 'auto',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box className="change-order-report-content" sx={{ p: 3, flex: 1, overflow: 'auto' }}>
            <ChangeOrderReport
              companyName={accountData?.account?.accountname || 'Unity Build Technologies'}
              workDescription={currentDocument?.description || ''}
              pcoNumber={currentDocument?.customid || ''}
              showDetailedBreakdown={currentSettings.showDetailedBreakdown}
              includeLaborRates={currentSettings.includeLaborRates}
              includeEquipmentCosts={currentSettings.showEquipmentSection}
              includeSubcontractorCosts={currentSettings.showSubcontractorSection}
              includeGeneralExpenses={currentSettings.showGeneralExpensesSection}
              showTaxDetails={currentSettings.showTaxDetails}
              showOverheadMarkup={currentSettings.showOverheadMarkup}
              showBaseCostColumn={currentSettings.showBaseCostColumn}
              showMaterialsSection={currentSettings.showMaterialsSection}
              showQuotesSection={currentSettings.showQuotesSection}
              showLaborSection={currentSettings.showLaborSection}
              showProjectTotalsBreakdown={currentSettings.showProjectTotalsBreakdown}
              showGlobalSupplyChainClause={currentSettings.showGlobalSupplyChainClause}
              showTariffsAndDutiesClause={currentSettings.showTariffsAndDutiesClause}
              showStandardContractTerms={currentSettings.showStandardContractTerms}
              showExtensionBreakouts={currentSettings.showExtensionBreakouts}
              showSubtotalInCostSummary={currentSettings.showSubtotalInCostSummary}
              showExtensionMaterialsInCostSummary={
                currentSettings.showExtensionMaterialsInCostSummary
              }
              showQuotedMaterialsInCostSummary={currentSettings.showQuotedMaterialsInCostSummary}
              showLaborInCostSummary={currentSettings.showLaborInCostSummary}
              showEquipmentInCostSummary={currentSettings.showEquipmentInCostSummary}
              showSubcontractorsInCostSummary={currentSettings.showSubcontractorsInCostSummary}
              showGeneralExpensesInCostSummary={currentSettings.showGeneralExpensesInCostSummary}
              showNotesInTables={currentSettings.showNotesInTables}
              showExpirationDate={currentSettings.showExpirationDate}
              expirationDate={getEffectiveExpirationDate()}
              isEstimateContext={isEstimateContext}
              onPrint={handlePrint}
              userData={userData}
              accountData={accountData}
            />
          </Box>
        </Box>
      </Box>

      {/* Work Description Maximized Editor */}
      <MaximizedTextEditor
        open={workDescriptionEditorOpen}
        onClose={() => setWorkDescriptionEditorOpen(false)}
        value={currentDocument?.description || ''}
        onChange={() => {}} // No-op since we handle saving via onSave
        onSave={handleWorkDescriptionSave}
        title={`${documentType} Description`}
        placeholder={`Describe the work to be performed for this ${documentType.toLowerCase()}...`}
        doneButtonText="Save & Close"
        maxWidth="lg"
        maxHeight="90vh"
        enableTemplates={false}
        enableWorkDescriptionTemplate={true} // Enable default template for work descriptions
      />

      {/* Profit Warning Dialog for Print/PDF Actions */}
      {profitMargins && (
        <ProfitWarning
          margins={profitMargins}
          variant="dialog"
          context={isEstimateContext ? 'estimate' : 'changeOrder'}
          showInDialog={showProfitWarningDialog}
          onClose={() => {
            setShowProfitWarningDialog(false);
            setPendingPrintAction(null);
          }}
          onProceedAnyway={executePendingAction}
        />
      )}
    </Box>
  );
};
